import { HTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'amber';
type BadgeSize = 'sm' | 'md';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
}

const variantStyles: Record<BadgeVariant, string> = {
  default:
    'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  success:
    'bg-green-100 text-green-700 border border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700/50',
  warning:
    'bg-yellow-100 text-yellow-700 border border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700/50',
  danger:
    'bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700/50',
  info:
    'bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700/50',
  amber:
    'bg-amber/20 text-amber-700 border border-amber/30 dark:text-amber',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'sm', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={clsx(
          'inline-flex items-center font-medium rounded',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Status-specific badge variants for common use cases
export const statusBadgeVariants: Record<string, BadgeVariant> = {
  // Project status
  discovery: 'info',
  in_progress: 'warning',
  review: 'info',
  complete: 'success',
  // Inquiry status
  new: 'amber',
  contacted: 'warning',
  converted: 'success',
  closed: 'default',
  // Payment status
  pending: 'warning',
  paid: 'success',
  failed: 'danger',
  refunded: 'default',
};

export default Badge;
